import { CustomList, Heading, TextLink } from '@cinch-labs/design-system'
import { IconNames } from '@cinch-labs/design-system/dist/src/tokens/tokens.types'
import { useViewport } from '@cinch-labs/shared-util'
import {
  StoryblokImage,
  getStoryblokImageServiceUrl,
} from '@cinch-nx/data-storyblok'

import { HomeImage, WhyCinchCard } from '../../types/home-story-content'
import styles from './why-cinch.module.css'
import Image from 'next/legacy/image'
import WhyCinchCardComponent from './why-cinch-card-component'

export interface WhyCinchProps {
  title: string
  image: StoryblokImage | HomeImage
  cards: WhyCinchCard[]
  fallback?: boolean
}

function isHomeImage(image: StoryblokImage | HomeImage): image is HomeImage {
  return 'component' in image && image.component === 'HomeImage'
}

const fallbackCards = [
  {
    title: 'Great value, high-quality cars',
    name: 'card1',
    icon: 'vehicle-front' as IconNames,
    body: (
      <p>
        With{' '}
        <a href="/used-cars" target="_blank">
          thousands of cars
        </a>{' '}
        all in one place, you’re sure to find your perfect one. All cars are
        checked by technical experts and come with a minimum 6-month MOT for
        total peace of mind.
      </p>
    ),
  },
  {
    title: 'Choose how you pay',
    name: 'card2',
    icon: 'calculator' as IconNames,
    body: (
      <p>
        Buy outright or spread the cost with helpful{' '}
        <a href="/car-finance" target="_blank">
          finance
        </a>{' '}
        options. You can{' '}
        <a href="/part-exchange" target="_blank">
          part exchange
        </a>{' '}
        and use the value of your old car to help pay for your new one.
      </p>
    ),
  },
  {
    title: 'Free delivery and 14 days to love it',
    name: 'card3',
    icon: 'home-delivery' as IconNames,
    body: (
      <p>
        Get{' '}
        <TextLink href="/delivery" key="delivery" target="_blank">
          free home delivery
        </TextLink>{' '}
        or{' '}
        <TextLink href="/car-collections" key="car-collections" target="_blank">
          collection
        </TextLink>{' '}
        when it suits you, with 14 days to love it or your money back and we’ll
        collect it for free.
      </p>
    ),
  },
]

const WhyCinch: React.FC<WhyCinchProps> = ({
  image,
  title,
  cards,
  fallback = false,
}) => {
  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })
  const isMobile = vw < 1024

  const sectionCards = fallback ? fallbackCards : cards

  return (
    <section className={styles['whyCinchContainer']} data-testid="whycinch">
      <div className={styles['whyCinchInner']}>
        <Heading fontSize="h1" className={styles['whyCinchHeading']}>
          {title}{' '}
        </Heading>

        <div className={styles['contentWrapper']}>
          <CustomList
            customClassNames={{
              bulletContainer: styles['bulletContainer'],
              listItem: styles['listItem'],
            }}
          >
            {sectionCards.map((card, idx) => (
              <WhyCinchCardComponent
                key={card.name ?? idx}
                title={card.title}
                name={card.name}
                icon={card.icon as IconNames}
                body={card.body}
              />
            ))}
          </CustomList>
          {!isMobile ? (
            <div
              className={styles['whyCinchImageContainer']}
              data-testid="featured-cards-image-section"
            >
              {isHomeImage(image) ? (
                <Image
                  src={image.filename}
                  alt={image.alt ?? ''}
                  layout="responsive"
                  width={360}
                  height={414}
                />
              ) : (
                <img
                  src={getStoryblokImageServiceUrl(image.filename, {
                    width: 360,
                    height: 414,
                    filters: [{ quality: 80 }],
                  })}
                  alt={image.alt ?? ''}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

export default WhyCinch
